import { Box, Stack, Typography } from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';
import Actions from './actions';
import { SlotInlineDetailsBelowRow } from './slot-inline-details-below-row';
import { useDroppable } from '@dnd-kit/core';
import CreateSessionInlineFormWrapper from './actions/add-session/create-session-inline-form';
import Item from './item';
import type { FlowDto, FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import React, { Fragment, useMemo } from 'react';
import { useSlotTimingPreviewMap } from './slot-timing-preview-store';
import { END_GAP_ANCHOR_ID } from './use-slot-list-form';
import { useFlowMode } from '../use-flow-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { useTranslations } from '@/i18n/use-translations';
import { flowControlInlinePerformMiddleWrapperSx } from '../flow-control-perform-expand';
import type { ScheduleInlineDetailsTab } from '../edit-details/schedule-inline-details-tab';
import { FLOW_CONTROL_SLOT_LIST_GAP, type FlowDetailMode } from '../flow-detail.types';
import type { VirtualInBetweenPlayout } from '@/helpers/flow-control-in-between-playout.helper';
import { isFlowControlSlotMediaBelowVisible } from '@/helpers/flow-control-playing-segment.helper';
import { FlowControlVirtualInBetweenPlayoutRow } from './flow-control-virtual-in-between-playout-row';
import { FlowControlInlineSlotMediaPanel } from './flow-control-inline-slot-media-panel';

type DroppableContainerProps = {
  id: string;
  children: React.ReactNode;
  minHeight?: number;
  listGap?: number;
};

const DroppableContainer = ({ id, children, minHeight, listGap = 1 }: DroppableContainerProps) => {
  const { setNodeRef } = useDroppable({ id });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: listGap,
        ...(minHeight ? { minHeight } : {}),
      }}
    >
      {children}
    </Box>
  );
};

type Props = {
  timedSlots: FlowSlotDto[];
  timedSlotsForDnd: FlowSlotDto[];
  calculationSlots?: FlowSlotDto[];
  containerId: string;
  minHeight?: number;
  isFormActive: boolean;
  formOffset: number;
  formDuration: number;
  formAvailableDuration: number;
  formSlotType: 'Session' | 'Break';
  formInsertBeforeSlotId?: string;
  formInsertAfterSlotId?: string;
  handleFormSuccess: () => void;
  handleFormClose: () => void;
  handleFormDurationChange: (duration: number) => void;
  handleShowForm: (
    gapSlotIdOrPosition: string,
    offset: number,
    slotType?: 'Session' | 'Break',
    insertBeforeSlotId?: string,
    insertAfterSlotId?: string,
  ) => void;
  refreshing: boolean;
  isHydrated: boolean;
  isSmallScreen: boolean;
  onSlotSelect?: (slot: FlowSlotDto, isUserInitiated?: boolean) => void;
  setSelectedSlot: (slot: FlowSlotDto | undefined) => void;
  selectedSlot?: FlowSlotDto;
  expandedSlotId: string | null;
  expandedFormType: 'timing' | 'new' | null;
  handleExpandedChange: (slotId: string | null) => void;
  handleFormTypeChange: (slotId: string, formType: 'timing' | 'new' | null) => void;
  handleSlotUpdate: (slot: FlowSlotDto) => void;
  handlePreviewSlotUpdate: (slot: FlowSlotDto) => void;
  onShowTimingAlert?: () => void;
  onShowPublishAlert?: () => void;
  cancelChanges?: () => void;
  handleSetEditingTiming: (editing: boolean, slotId: string) => void;
  editingSlotId: string | null;
  editingEditSlotId: string | null;
  previewFlow?: FlowDto;
  flowEndOffset?: number;
  setShowFlowDetails: React.Dispatch<React.SetStateAction<import('../flow-detail.types').FlowDetailMode | undefined>>;
  setFlowDetailsDirection: React.Dispatch<React.SetStateAction<'up' | 'down' | undefined>>;
  flowStartOffset: number;
  dragTimedIds?: string[] | null;
  disableDrop?: boolean;
  modeOverride?: import('@/types/entity-mode').Mode;
  rightSectionOnlyPublishIcon?: boolean;
  middleSectionOpensOverlayOnSmallScreen?: boolean;
  /** When set (e.g. schedule tab), wide-screen delete uses this instead of switching to flow settings */
  onWideScreenSlotDeletedFromList?: () => void;
  onSessionPublishSuccess?: () => void;
  flowControlInlinePerform?: boolean;
  flowControlPerformExpandedKey?: string | null;
  onFlowControlPerformExpand?: (key: string | null) => void;
  renderFlowControlPerformMiddle?: (
    slot: import('@/types/dtos/flows/response/flow.dto').FlowSlotDto,
  ) => React.ReactNode;
  /** Narrow Slot Assets: which slot shows inline details under the row */
  slotAssetsInlineDetailsSlotId?: string | null;
  showSlotAssetsInlineDetails?: boolean;
  onSlotAssetsInlineDetailsToggle?: (slotId: string) => void;
  onSlotAssetsInlineDetailsClose?: (slotId: string) => void;
  /** Which inline panel to show under the slot on small screens (with `middleSectionOpensOverlayOnSmallScreen`) */
  smallScreenInlineDetailsVariant?: 'slotAssets' | 'schedule';
  inlineScheduleDetailsTab?: ScheduleInlineDetailsTab;
  onInlineScheduleDetailsTabChange?: (tab: ScheduleInlineDetailsTab) => void;
  eventSlotIdParam?: string | null;
  /** When `flowSlotAssets`, wide-screen middle section selects for assets panel instead of timing */
  startRowMediaOpenDetailsMode?: FlowDetailMode;
  readOnly?: boolean;
  /** Empty timed area: hide NEW/BREAK when the end-gap row (fixed end-time) already shows them. */
  suppressEmptyTimedWizardBreak?: boolean;
  /** Gap rows before fixed-start slots / end gap: hide NEW/BREAK in read-only assets & control columns. */
  hideGapSlotWizardAndBreak?: boolean;
  showSlotDragHandle?: boolean;
  /** Schedule tab while live: timed slots at/before the playing segment */
  lockedTimedSlotIds?: ReadonlySet<string>;
  /** While dragging across sections, avoid mounting the large empty placeholder (layout thrash on mobile). */
  hideEmptyPlaceholderWhileDragging?: boolean;
  flowControlInlineMedia?: boolean;
  flowControlInlineMediaSlotId?: string | null;
  flowControlPlayingExpandId?: string | null;
  onFlowControlInlineMediaToggle?: (slotId: string) => void;
  virtualInBetweenPlayout?: VirtualInBetweenPlayout | null;
  rightSectionPlayIcon?: boolean;
};

const isUntimedSlot = (slot: FlowSlotDto) =>
  slot.isUntimed ?? (slot.plannedOffsetMinutes === undefined && slot.computedOffsetMinutes === undefined);

export const TimedSection = ({
  timedSlots,
  timedSlotsForDnd,
  calculationSlots,
  containerId,
  minHeight,
  isFormActive,
  formOffset,
  formDuration,
  formAvailableDuration,
  formSlotType,
  formInsertBeforeSlotId,
  formInsertAfterSlotId,
  handleFormSuccess,
  handleFormClose,
  handleFormDurationChange,
  handleShowForm,
  refreshing,
  isHydrated,
  isSmallScreen,
  onSlotSelect,
  setSelectedSlot,
  selectedSlot,
  expandedSlotId,
  expandedFormType,
  handleExpandedChange,
  handleFormTypeChange,
  handleSlotUpdate,
  handlePreviewSlotUpdate,
  onShowTimingAlert,
  onShowPublishAlert,
  cancelChanges,
  handleSetEditingTiming,
  editingSlotId,
  editingEditSlotId,
  previewFlow,
  flowEndOffset,
  setShowFlowDetails,
  flowStartOffset,
  dragTimedIds,
  disableDrop = false,
  modeOverride,
  rightSectionOnlyPublishIcon = false,
  middleSectionOpensOverlayOnSmallScreen = false,
  onWideScreenSlotDeletedFromList,
  onSessionPublishSuccess,
  flowControlInlinePerform = false,
  flowControlPerformExpandedKey = null,
  onFlowControlPerformExpand,
  renderFlowControlPerformMiddle,
  slotAssetsInlineDetailsSlotId = null,
  showSlotAssetsInlineDetails = false,
  onSlotAssetsInlineDetailsToggle,
  onSlotAssetsInlineDetailsClose,
  smallScreenInlineDetailsVariant,
  inlineScheduleDetailsTab,
  onInlineScheduleDetailsTabChange,
  eventSlotIdParam,
  startRowMediaOpenDetailsMode,
  readOnly = false,
  suppressEmptyTimedWizardBreak = false,
  hideGapSlotWizardAndBreak = false,
  showSlotDragHandle = false,
  lockedTimedSlotIds,
  hideEmptyPlaceholderWhileDragging = false,
  flowControlInlineMedia = false,
  flowControlInlineMediaSlotId = null,
  flowControlPlayingExpandId = null,
  onFlowControlInlineMediaToggle,
  virtualInBetweenPlayout = null,
  rightSectionPlayIcon = false,
}: Props) => {
  const flowModeResolved = useFlowMode();
  const mode = modeOverride ?? flowModeResolved;
  const t = useTranslations();
  const previewMap = useSlotTimingPreviewMap();
  const normalizedInsertAfterSlotId = formInsertAfterSlotId === END_GAP_ANCHOR_ID ? undefined : formInsertAfterSlotId;
  const shouldRenderEmptyForm = timedSlots.length === 0 && isFormActive;
  const gapBeforeBySlotId = useMemo(() => {
    const result = new Map<string, { startOffset: number; duration: number }>();
    let previousEnd = 0;
    const slotsForGap = (calculationSlots ?? timedSlots).filter((slot) => !isUntimedSlot(slot));
    slotsForGap.forEach((slot) => {
      if (slot.id.startsWith('temp-')) {
        const tempOffset = slot.computedOffsetMinutes ?? 0;
        const tempDuration = slot.computedDurationMinutes ?? 0;
        const tempEnd =
          (Number.isFinite(tempOffset) ? tempOffset : 0) + (Number.isFinite(tempDuration) ? tempDuration : 0);
        if (Number.isFinite(tempEnd)) {
          previousEnd = tempEnd;
        }
        return;
      }
      const preview = previewMap.get(slot.id);
      const slotOffset = preview?.offsetMinutes ?? slot.computedOffsetMinutes ?? 0;
      const slotDuration = preview?.durationMinutes ?? slot.computedDurationMinutes ?? 0;
      const safeOffset = Number.isFinite(slotOffset) ? slotOffset : 0;
      const safeDuration = Number.isFinite(slotDuration) ? slotDuration : 0;
      if (slot.plannedOffsetMinutes !== undefined && Number.isFinite(slot.plannedOffsetMinutes)) {
        const gapDuration = slot.plannedOffsetMinutes - previousEnd;
        if (gapDuration > 0) {
          result.set(slot.id, { startOffset: previousEnd, duration: gapDuration });
        }
      }
      const nextEnd = safeOffset + safeDuration;
      if (Number.isFinite(nextEnd)) {
        previousEnd = nextEnd;
      }
    });
    return result;
  }, [calculationSlots, timedSlots, previewMap]);
  return (
    <DroppableContainer
      id={containerId}
      minHeight={minHeight}
      listGap={flowControlInlineMedia ? FLOW_CONTROL_SLOT_LIST_GAP : 1}
    >
      {timedSlots.length === 0 && !isFormActive ? (
        hideEmptyPlaceholderWhileDragging ? (
          <Box aria-hidden sx={{ minHeight: minHeight ?? 64, flexShrink: 0 }} />
        ) : (
          <Box
            sx={{
              minHeight: minHeight ?? 64,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              px: 3,
              py: 3,
              textAlign: 'center',
            }}
          >
            {isOrganizeMode(mode) ? (
              <Box component="span" sx={{ color: 'text.secondary' }}>
                {t('flows.detail.slotList.noScheduledSessionsSharedYet', {
                  defaultValue: 'No scheduled sessions shared yet',
                })}
              </Box>
            ) : (
              <>
                <AutoAwesome sx={{ fontSize: 40, color: 'info.main', mb: 0.5 }} />
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                  {t(
                    startRowMediaOpenDetailsMode === 'flowSlotAssets'
                      ? 'flows.detail.slotAssets.timedEmpty'
                      : 'flows.detail.slotList.timedEmpty',
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
                  {t(
                    startRowMediaOpenDetailsMode === 'flowSlotAssets'
                      ? 'flows.detail.slotAssets.timedEmptyHint'
                      : 'flows.detail.slotList.timedEmptyHint',
                  )}
                </Typography>
              </>
            )}
            {!readOnly && !isOrganizeMode(mode) ? (
              <Stack direction="row" justifyContent="center" sx={{ width: '100%', mt: 1 }}>
                <Actions
                  centered
                  offset={0}
                  duration={formAvailableDuration}
                  setSelectedSlot={setSelectedSlot}
                  showWizardAndBreak={!suppressEmptyTimedWizardBreak}
                  onShowForm={(slotType) => {
                    handleShowForm('gap-after-end', 0, slotType ?? 'Session');
                  }}
                />
              </Stack>
            ) : null}
          </Box>
        )
      ) : null}
      {shouldRenderEmptyForm && (
        <CreateSessionInlineFormWrapper
          offset={formOffset}
          durationMinutes={formAvailableDuration}
          initialPlannedDurationMinutes={formDuration}
          onSuccess={handleFormSuccess}
          onClose={handleFormClose}
          setSelectedSlot={setSelectedSlot}
          onFormDurationChange={handleFormDurationChange}
          slotType={formSlotType}
          insertBeforeSlotId={formInsertBeforeSlotId}
          insertAfterSlotId={normalizedInsertAfterSlotId}
        />
      )}
      {timedSlots.map((slot) => {
        // Check if form should appear before current slot
        const shouldShowFormBefore = isFormActive && formInsertBeforeSlotId === slot.id;
        // Check if form should appear after current slot
        const shouldShowFormAfter = isFormActive && normalizedInsertAfterSlotId === slot.id;
        const gapBefore = gapBeforeBySlotId.get(slot.id);
        const slotIndex = timedSlotsForDnd.findIndex((s) => s.id === slot.id);
        const gapBeforeInsertAfterSlotId = slotIndex > 0 ? timedSlotsForDnd[slotIndex - 1]?.id : undefined;
        const forceTimedPreview = !!dragTimedIds?.includes(slot.id) && isUntimedSlot(slot);

        // Helper function to render the slot item (used for both gap and non-gap slots)
        const renderSlotItem = () => {
          return (
            <Fragment key={slot.id}>
              <Item
                slot={slot}
                containerId={containerId}
                forceTimedPreview={forceTimedPreview}
                gapBefore={gapBefore ?? undefined}
                gapBeforeInsertAfterSlotId={gapBeforeInsertAfterSlotId}
                hideGapSlotWizardAndBreak={hideGapSlotWizardAndBreak}
                expandedSlotId={expandedSlotId}
                onExpandedChange={handleExpandedChange}
                expandedFormType={expandedSlotId === slot.id ? expandedFormType : null}
                onFormTypeChange={handleFormTypeChange}
                isSmallScreen={isSmallScreen}
                onClick={(userInitiated = true) => {
                  if (onSlotSelect) {
                    onSlotSelect(slot, userInitiated);
                    if (isSmallScreen) {
                      requestAnimationFrame(() => {
                        const el = document.querySelector(`[data-slot-id="${slot.id}"]`);
                        if (el && 'scrollIntoView' in el) {
                          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      });
                    }
                  } else {
                    setSelectedSlot(slot);
                  }
                }}
                locked={slot?.plannedOffsetMinutes !== undefined}
                refreshing={refreshing}
                hydrated={isHydrated}
                shortenedBy={
                  slot.plannedDurationMinutes !== undefined &&
                  slot.computedDurationMinutes !== undefined &&
                  slot.plannedDurationMinutes > slot.computedDurationMinutes
                    ? slot.plannedDurationMinutes - slot.computedDurationMinutes
                    : 0
                }
                selected={
                  flowControlInlineMedia ? flowControlInlineMediaSlotId === slot.id : selectedSlot?.id === slot.id
                }
                onSlotUpdate={handleSlotUpdate}
                onPreviewSlotUpdate={handlePreviewSlotUpdate}
                onShowTimingAlert={onShowTimingAlert}
                onShowPublishAlert={onShowPublishAlert}
                cancelChanges={cancelChanges}
                setEditingTiming={(editing) => handleSetEditingTiming(editing, slot.id)}
                isEditingTiming={editingSlotId === slot.id}
                isEditingEdit={editingEditSlotId === slot.id}
                localSlots={timedSlots}
                previewFlow={previewFlow}
                flowEndOffset={flowEndOffset}
                listFlowStartOffset={flowStartOffset}
                onSlotDeleted={() => {
                  if (!isSmallScreen) {
                    if (onWideScreenSlotDeletedFromList) {
                      onWideScreenSlotDeletedFromList();
                    } else {
                      setShowFlowDetails('flowSettings');
                    }
                  }
                }}
                onShowForm={handleShowForm}
                setSelectedSlot={setSelectedSlot}
                disableDrop={disableDrop}
                modeOverride={modeOverride}
                rightSectionOnlyPublishIcon={rightSectionOnlyPublishIcon}
                middleSectionOpensOverlayOnSmallScreen={middleSectionOpensOverlayOnSmallScreen}
                onSessionPublishSuccess={onSessionPublishSuccess}
                flowControlInlinePerform={flowControlInlinePerform}
                flowControlPerformExpandedKey={flowControlPerformExpandedKey}
                onFlowControlPerformExpand={onFlowControlPerformExpand}
                onSlotAssetsInlineDetailsToggle={onSlotAssetsInlineDetailsToggle}
                onSlotAssetsInlineDetailsClose={onSlotAssetsInlineDetailsClose}
                slotInlineDetailsOpen={Boolean(
                  showSlotAssetsInlineDetails && slotAssetsInlineDetailsSlotId === slot.id,
                )}
                smallScreenInlineDetailsVariant={smallScreenInlineDetailsVariant}
                inlineScheduleDetailsTab={inlineScheduleDetailsTab}
                onInlineScheduleDetailsTabChange={onInlineScheduleDetailsTabChange}
                startRowMediaOpenDetailsMode={startRowMediaOpenDetailsMode}
                showSlotDragHandle={showSlotDragHandle}
                scheduleLiveLockBlocked={Boolean(lockedTimedSlotIds?.has(slot.id))}
                flowControlInlineMedia={flowControlInlineMedia}
                onFlowControlInlineMediaToggle={onFlowControlInlineMediaToggle}
                flowControlInlineMediaOpen={Boolean(
                  flowControlInlineMedia &&
                  isFlowControlSlotMediaBelowVisible(slot.id, flowControlInlineMediaSlotId, flowControlPlayingExpandId),
                )}
                rightSectionPlayIcon={rightSectionPlayIcon}
              />
              {flowControlInlineMedia && (
                <FlowControlInlineSlotMediaPanel
                  slot={slot}
                  expandId={flowControlInlineMediaSlotId}
                  playingExpandId={flowControlPlayingExpandId}
                />
              )}
              {flowControlInlineMedia && virtualInBetweenPlayout?.insertAfterSlotId === slot.id && (
                <FlowControlVirtualInBetweenPlayoutRow
                  virtual={virtualInBetweenPlayout}
                  isExpanded={flowControlInlineMediaSlotId === virtualInBetweenPlayout.segmentId}
                  onToggleExpand={() => onFlowControlInlineMediaToggle?.(virtualInBetweenPlayout.segmentId)}
                />
              )}
              {flowControlInlinePerform &&
                flowControlPerformExpandedKey === slot.id &&
                renderFlowControlPerformMiddle && (
                  <Box sx={{ ...flowControlInlinePerformMiddleWrapperSx, mx: 0 }}>
                    {renderFlowControlPerformMiddle(slot)}
                  </Box>
                )}
              <SlotInlineDetailsBelowRow
                slot={slot}
                activeSlotId={slotAssetsInlineDetailsSlotId}
                show={Boolean(showSlotAssetsInlineDetails)}
                variant={smallScreenInlineDetailsVariant}
                flowStartOffset={flowStartOffset}
                eventSlotIdParam={eventSlotIdParam}
                onWideScreenSlotDeletedFromList={onWideScreenSlotDeletedFromList}
                onSessionPublishSuccess={onSessionPublishSuccess}
                stackMarginX={0}
                inlineScheduleDetailsTab={inlineScheduleDetailsTab}
                slotAssetsListViewMode={mode}
              />
            </Fragment>
          );
        };

        // Show form before current slot if insertBeforeSlotId matches
        if (shouldShowFormBefore) {
          // Calculate offset: use the slot's offset (form will appear before it)
          const content = (
            <React.Fragment key={`fragment-before-${slot.id}`}>
              <CreateSessionInlineFormWrapper
                key={`form-before-${slot.id}`}
                offset={formOffset}
                durationMinutes={formAvailableDuration}
                initialPlannedDurationMinutes={formDuration}
                onSuccess={handleFormSuccess}
                onClose={handleFormClose}
                setSelectedSlot={setSelectedSlot}
                onFormDurationChange={handleFormDurationChange}
                slotType={formSlotType}
                insertBeforeSlotId={formInsertBeforeSlotId}
                insertAfterSlotId={normalizedInsertAfterSlotId}
              />
              {renderSlotItem()}
            </React.Fragment>
          );

          return content;
        }

        // Render slot, and if form should appear after it, render form after slot
        const slotItem = renderSlotItem();

        if (shouldShowFormAfter) {
          const content = (
            <React.Fragment key={`fragment-after-${slot.id}`}>
              {slotItem}
              <CreateSessionInlineFormWrapper
                key={`form-after-${slot.id}`}
                offset={formOffset}
                durationMinutes={formAvailableDuration}
                initialPlannedDurationMinutes={formDuration}
                onSuccess={handleFormSuccess}
                onClose={handleFormClose}
                setSelectedSlot={setSelectedSlot}
                onFormDurationChange={handleFormDurationChange}
                slotType={formSlotType}
                insertBeforeSlotId={formInsertBeforeSlotId}
                insertAfterSlotId={normalizedInsertAfterSlotId}
              />
            </React.Fragment>
          );

          return content;
        }

        return <React.Fragment key={`fragment-${slot.id}`}>{slotItem}</React.Fragment>;
      })}
    </DroppableContainer>
  );
};

export default TimedSection;
